import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Poshe Styles';
const DEFAULT_DESCRIPTION = 'Poshe Styles — Premium ladies bags and accessories store in India. Shop handbags, clutches, totes, wallets, sling bags, backpacks and more. Carry Your World in Style.';
const DEFAULT_IMAGE = '/images/og-default.jpg'; // OG image in public/images/
const SITE_URL = 'https://poshestyles.com';
const DEFAULT_KEYWORDS = 'Poshe Styles, PosheStyles, poshe style, poshe styles bags, premium ladies bags, ladies bags online India, designer handbags India, buy handbags online, women handbags, clutches, totes, wallets, sling bags, backpacks, premium bags, luxury bags India, affordable designer bags, leather bags women, office bags for women, party clutches, branded bags India';

/**
 * SEO component for dynamic meta tags on every page.
 * Renders <head> meta tags via react-helmet-async so crawlers
 * and social platforms (WhatsApp, Facebook, Twitter) can parse them.
 *
 * Includes:
 * - Primary meta tags (title, description, keywords, canonical)
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - Organization structured data (logo in search results)
 * - WebSite structured data (sitelinks search box)
 * - Product structured data (for product pages)
 * - BreadcrumbList structured data
 *
 * Usage:
 *   <SEO
 *     title="Shop Handbags"
 *     description="Browse our premium handbag collection"
 *     image="https://res.cloudinary.com/..."
 *     url="/products"
 *     keywords="custom, keywords, here"
 *     breadcrumbs={[
 *       { name: 'Home', url: '/' },
 *       { name: 'Products', url: '/products' },
 *     ]}
 *   />
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image,
  url,
  type = 'website',
  product,        // optional Product object for structured data
  noIndex = false,
  keywords = DEFAULT_KEYWORDS,
  breadcrumbs,    // optional array of { name, url } for BreadcrumbList
}) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || SITE_URL;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Premium Ladies Bags & Accessories | Carry Your World in Style`;
  const canonicalUrl = url ? `${siteUrl}${url}` : siteUrl;
  const ogImage = image || `${siteUrl}${DEFAULT_IMAGE}`;

  // Organization structured data — enables logo in Google search results
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    alternateName: ['PosheStyles', 'Poshe Style', 'poshe styles'],
    url: siteUrl,
    logo: `${siteUrl}/images/og-default.jpg`,
    image: `${siteUrl}/images/og-default.jpg`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [
      'https://instagram.com/poshe_styles',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['English', 'Hindi'],
    },
  };

  // WebSite structured data — enables sitelinks search box
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: ['PosheStyles', 'Poshe Style'],
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/products?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // BreadcrumbList structured data
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${siteUrl}${crumb.url}`,
    })),
  } : null;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Organization structured data — for logo in Google search */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* WebSite structured data — for sitelinks search box */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {/* BreadcrumbList structured data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Product structured data (JSON-LD) */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.name,
            description: product.description,
            image: product.images?.map((img) =>
              img.startsWith('http') ? img : `${siteUrl}${img}`
            ) || [],
            brand: {
              '@type': 'Brand',
              name: SITE_NAME,
            },
            sku: product._id,
            category: product.category,
            offers: {
              '@type': 'Offer',
              url: canonicalUrl,
              priceCurrency: 'INR',
              price: product.salePrice && product.saleEndTime && new Date(product.saleEndTime) > new Date()
                ? product.salePrice
                : product.price,
              itemCondition: 'https://schema.org/NewCondition',
              availability: product.stock > 0
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
              priceValidUntil: product.saleEndTime
                ? new Date(product.saleEndTime).toISOString().split('T')[0]
                : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            },
            ...(product.numReviews > 0 && {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: product.rating?.toFixed(1),
                reviewCount: product.numReviews,
                bestRating: 5,
                worstRating: 1,
              },
            }),
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
